import { Button } from "@components/common/button"
import { DictType } from "@pma/types"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@components/common/alert-dialog"
import axios from "axios"
import { parseCookies } from "nookies"
import { useMutation } from "@tanstack/react-query"
import { toast } from "@hooks/use-toast"
import { ACCESS_TOKEN, queryClient } from "@pma/utils/constants"
import {
  DELETE_FILE_ENDPOINT,
  DELETE_MULTIPLE_FILE_ENDPOINT,
  SERVER_URL,
} from "@pma/utils/api-routes"
import { LucideTrash, X } from "lucide-react"
import useTranslator from "@hooks/use-translator"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useState } from "react"
import { cn } from "@pma/utils/helpers"

interface Props {
  file?: DictType
  sm?: boolean
  fileArr?: DictType[]
  handleBatchSelection?: any
  onClose?: Function
  showBatchSelection?: boolean
  isMenuView?: boolean
  setFolderItems?: React.Dispatch<React.SetStateAction<{ [id: number]: any[] }>>
  setBreadcrumbs?: React.Dispatch<React.SetStateAction<{ [id: number]: any[] }>>
  foldersMeta?: { [id: number]: number }
  setRecentDeletedFile?: React.Dispatch<React.SetStateAction<any[]>>
  refetchFolderContent?: (folderId: number) => void
}
const DeleteFile = ({
  file,
  sm,
  fileArr = [],
  handleBatchSelection,
  onClose,
  showBatchSelection = false,
  isMenuView = false,
  setFolderItems,
  setBreadcrumbs,
  foldersMeta,
  setRecentDeletedFile,
  refetchFolderContent,
}: Props) => {
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const [isDeleting, setIsDeleting] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)

  const navigate = useNavigate()

  const deleteFile = async () => {
    if (fileArr && fileArr.length > 0) {
      const ids = fileArr.map((val) => val.id)
      return await axios
        .delete(`${SERVER_URL}${DELETE_MULTIPLE_FILE_ENDPOINT}`, {
          data: {
            file_id: ids,
            delete_from: "all",
          },
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        })
        .then((val) => val.data)
    } else {
      return await axios
        .delete(SERVER_URL + DELETE_FILE_ENDPOINT + `/${file?.id}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        })
        .then((val) => val.data)
    }
  }

  const deleteFileMutation = useMutation({
    mutationKey: ["delete-file", file?.id],
    mutationFn: deleteFile,
    onSuccess: (data) => {
      if (setFolderItems && foldersMeta) {
        const fileIds = fileArr.length > 0 ? fileArr.map((f) => f.id) : file ? [file.id] : []
        const sourceFolderIds = new Set<number>()
        let targetFolderId = 0
        if (setRecentDeletedFile) {
          setRecentDeletedFile(fileIds)
        }

        fileIds.forEach((fileId) => {
          const sourceFolder = foldersMeta[fileId]
          targetFolderId = foldersMeta[fileId]
          if (sourceFolder) {
            sourceFolderIds.add(sourceFolder)
          }
        })

        setFolderItems((prev) => {
          const updatedItems = { ...prev }
          sourceFolderIds.forEach((folderId) => {
            updatedItems[folderId] = prev[folderId].filter((item) => !fileIds.includes(item.id))
          })
          return updatedItems
        })

        if (refetchFolderContent) {
          refetchFolderContent(targetFolderId)
        }
      }

      if (fileArr && fileArr.length > 0) {
        handleBatchSelection()
        queryClient.invalidateQueries({ queryKey: ["get-curr-folder-files"] })
        queryClient.invalidateQueries({ queryKey: ["grouped-tasks"] })
      }
      toast({
        title: translate("Success"),
        description: `${fileArr.length === 0 ? 1 : fileArr.length} ${translate("file has been deleted")}`,
        variant: "success",
      })
      queryClient.invalidateQueries({ queryKey: ["grouped-tasks"] })
      newParams.set("revalidate", "tasks")
      navigate(`?${newParams.toString()}`)
      setIsOpen(false)
      setIsDeleting(false)
    },
    onError: (error) => {
      toast({
        title: translate("Error"),
        description: `${translate("Unable to delete")} ${file?.name}`,
        variant: "error",
      })
      setIsOpen(false)
      setIsDeleting(false)
    },
  })

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip>
        <TooltipTrigger>
          <AlertDialogTrigger
            disabled={
              (showBatchSelection && fileArr.length === 0) || (!showBatchSelection && !file)
            }
          >
            {showBatchSelection ? (
              <Button
                variant='outline'
                className={`flex flex-row gap-1 px-2 ${locale === "he" ? "sm:w-[120px] md:w-min" : "sm:w-[130px] md:w-min"} items-center justify-between text-red-400 hover:bg-slate-300`}
                size={"sm"}
              >
                <LucideTrash className='text-red-500' size={sm ? 10 : 16} />
                <span className='hidden min-w-[30px] flex-1 text-left text-[12px] sm:block sm:text-[10px]'>
                  {`${translate("Delete selected files")}`}
                </span>
              </Button>
            ) : isMenuView ? (
              <div className='flex items-center gap-2'>
                <Button variant={"ghost"} className='h-6 w-7 text-xs' size={"icon"}>
                  <LucideTrash className='text-red-500' size={sm ? 10 : 16} />
                </Button>
                <span className='text-sm'>{`${translate("Delete file")}`}</span>
              </div>
            ) : (
              <Button variant={"ghost"} className='h-6 w-7 text-xs' size={"icon"}>
                <LucideTrash className='text-red-500' size={sm ? 10 : 16} />
              </Button>
            )}
          </AlertDialogTrigger>
        </TooltipTrigger>
        {!isMenuView && (
          <TooltipContent className='bg-destructive'>
            {showBatchSelection ? translate("Delete selected files") : translate("Delete file")}
          </TooltipContent>
        )}
      </Tooltip>
      <AlertDialogContent className={isHebrew ? "rtl" : "ltr"}>
        <AlertDialogHeader className={isHebrew ? "rtl" : "ltr"}>
          <AlertDialogTitle className={isHebrew ? "rtl" : "ltr"}>
            {translate("Proceed to delete")} {fileArr.length > 0 ? fileArr.length : null}{" "}
            {fileArr.length > 0 ? (
              <span className='text-red-600'>{translate("Selected files")}</span>
            ) : (
              file?.name
            )}
            ?
          </AlertDialogTitle>
          <AlertDialogDescription className={isHebrew ? "rtl" : "ltr"}>
            {translate("This file will be moved to trash")}.
          </AlertDialogDescription>
          <AlertDialogDescription className={cn('text-orange-500', isHebrew ? "rtl" : "ltr")}>
            {translate("Please note, the file will be permanently deleted after 30 days")}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => {
              if (handleBatchSelection) {
                handleBatchSelection()
              }
              setIsOpen(false)
            }}
          >
            {translate("Cancel")}
          </AlertDialogCancel>
          <Button
            className='bg-destructive'
            disabled={deleteFileMutation?.isPending || isDeleting}
            onClick={() => {
              setIsDeleting(true)
              deleteFileMutation.mutate()
            }}
          >
            {isDeleting ? translate("Deleting...") : translate("Continue")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default DeleteFile
